import React, { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiGrid, FiCalendar, FiInbox, FiCreditCard, FiUsers, FiUserCheck, FiShield, FiTruck, FiBell, FiSettings, FiMenu, FiArrowUpRight } from 'react-icons/fi';
import { State } from '../state/reducers';
import { isTeamAdminUser } from '../util/teamAdmin';

const navigation = [
  { label: '營運工作台', items: [
    { to: '/', label: '團隊概要', icon: FiGrid },
    { to: '/brand-orders', label: '品牌派單', icon: FiInbox },
    { to: '/orders', label: '訂單列表', icon: FiCalendar },
    { to: '/commission-payments', label: '佣金付款', icon: FiCreditCard },
  ] },
  { label: '車隊與人員', items: [
    { to: '/users', label: '用戶列表', icon: FiUsers },
    { to: '/drivers/approval', label: '待核可司機', icon: FiUserCheck },
    { to: '/lead-applications', label: '代理申請', icon: FiShield, adminOnly: true },
    { to: '/vehicles', label: '車輛型別', icon: FiTruck },
  ] },
  { label: '管理設定', items: [
    { to: '/announcements', label: '公告發送', icon: FiBell },
    { to: '/account-info', label: '帳號資訊', icon: FiSettings },
  ] },
];

function pageContext(path: string) {
  if (path === '/') return { kind: 'overview', eyebrow: 'OVERVIEW', description: '掌握車隊近況，從待辦事項開始今天的營運。' };
  if (path === '/orders/create' || path.startsWith('/orders/edit/')) return { kind: 'order-form', eyebrow: 'ORDER MANAGEMENT', description: '設定行程、乘客資料與費用，確認內容後儲存訂單。' };
  if (path.endsWith('/refund')) return { kind: 'refund', eyebrow: 'REFUND MANAGEMENT', description: '核對訂單與付款資訊，再設定退款金額與處理方式。' };
  if (path === '/orders') return { kind: 'calendar', eyebrow: 'ORDER MANAGEMENT', description: '依日期檢視行程與派車狀態，點選日期查看當日訂單。' };
  if (path.startsWith('/orders/')) return { kind: 'order-detail', eyebrow: 'ORDER DETAILS', description: '檢視行程、乘客與付款資訊，處理派車及訂單作業。' };
  if (path === '/brand-orders') return { kind: 'brand', eyebrow: 'BRAND DISPATCH', description: '確認品牌送達的行程、服務要求與承接金額。' };
  if (path === '/users') return { kind: 'users', eyebrow: 'TEAM DIRECTORY', description: '搜尋團隊司機，查看聯絡資料、角色與司機檔案。' };
  if (path === '/drivers/approval') return { kind: 'approval', eyebrow: 'DRIVER REVIEW', description: '核對待審核司機的個人與車輛資料，完成加入車隊審核。' };
  if (path.startsWith('/drivers/')) return { kind: 'driver-form', eyebrow: 'DRIVER PROFILE', description: '維護司機聯絡方式、車輛、證件與收款資料。' };
  if (path === '/lead-applications') return { kind: 'approval', eyebrow: 'ACCESS REVIEW', description: '檢視代理隊長申請，確認人員後核准或拒絕。' };
  if (path === '/vehicles') return { kind: 'vehicles', eyebrow: 'VEHICLE CATALOG', description: '維護車型、乘客上限與行李配置，提供一致的接送規格。' };
  if (path === '/commission-payments') return { kind: 'payments', eyebrow: 'COMMISSION PAYMENTS', description: '依付款狀態追蹤佣金，開啟明細核對金額及轉帳證明。' };
  if (path.startsWith('/commission-payments/')) return { kind: 'payment-detail', eyebrow: 'PAYMENT DETAILS', description: '核對收付款資料、轉帳證明與付款紀錄。' };
  if (path === '/announcements') return { kind: 'announcement', eyebrow: 'TEAM COMMUNICATION', description: '撰寫車隊公告，確認標題與內容後發送。' };
  return { kind: 'account', eyebrow: 'ACCOUNT & SETTINGS', description: '檢視登入身分與車隊管理設定。' };
}

interface AdminDashboardProps { children: ReactNode; title: string; }

const AdminDashboard: React.FC<AdminDashboardProps> = ({ children, title }) => {
  const user = useSelector((state: State) => state.user);
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const context = pageContext(pathname);
  useEffect(() => {
    document.body.classList.add('fleet-admin');
    return () => document.body.classList.remove('fleet-admin');
  }, []);
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  return (
    <div className="fleet-shell">
      <a className="fleet-skip" href="#fleet-main">跳至主要內容</a>
      <aside className="fleet-sidebar">
        <Link to="/" className="fleet-brand"><span className="fleet-brand-mark">E</span><span>Easyride<small>車隊管理系統</small></span></Link>
        <div className="fleet-team"><span>目前管理車隊</span><strong>{user?.team?.name || '車隊工作台'}</strong></div>
        <button type="button" className="fleet-menu-toggle" aria-expanded={menuOpen} aria-controls="fleet-navigation" onClick={() => setMenuOpen(!menuOpen)}><FiMenu aria-hidden="true" /> {menuOpen ? '收合導覽' : '開啟導覽'}</button>
        <nav id="fleet-navigation" className={menuOpen ? 'fleet-navigation is-open' : 'fleet-navigation'} aria-label="車隊管理導覽">
          {navigation.map(group => <div className="fleet-nav-group" key={group.label}>
            <p>{group.label}</p>
            {group.items.filter(item => !('adminOnly' in item && item.adminOnly) || isTeamAdminUser(user)).map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => `fleet-nav-link${isActive || (item.to === '/users' && pathname.startsWith('/drivers/') && pathname !== '/drivers/approval') ? ' is-active' : ''}`}><item.icon aria-hidden="true" /><span>{item.label}</span></NavLink>)}
          </div>)}
        </nav>
        <div className="fleet-sidebar-footer">Easyride <span>營運管理工作台</span></div>
      </aside>
      <div className="fleet-workspace">
        <header className="fleet-topbar"><div className="fleet-breadcrumb"><Link to="/">車隊管理</Link><span aria-hidden="true">/</span><span>{title}</span></div><Link to="/account-info" className="fleet-profile"><span className="fleet-avatar">{user?.displayName?.slice(0, 1) || 'E'}</span><span>{user?.displayName || '帳號資訊'}</span></Link></header>
        <main id="fleet-main" tabIndex={-1} className={`fleet-page fleet-page--${context.kind}`}>
          <div className="fleet-page-heading"><div><span className="fleet-eyebrow">{context.eyebrow}</span><h1>{title}</h1><p>{context.description}</p></div><span className="fleet-workspace-label">車隊營運 <FiArrowUpRight aria-hidden="true" /></span></div>
          <div className="fleet-page-content">{children}</div>
        </main>
      </div>
    </div>
  );
};
export default AdminDashboard;
